<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <div
    class="group flex h-10 min-w-0 items-center gap-2 rounded-xl px-2 py-1 transition-all duration-200"
    :class="{
      'bg-gradient-to-r from-n-brand/15 to-n-brand/5 text-n-brand font-semibold ring-1 ring-n-brand/15 shadow-sm':
        isActive && !hasActiveChild,
      'text-n-slate-12 font-medium': hasActiveChild,
      'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
    }"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      class="flex h-full min-w-0 flex-1 items-center gap-2"
      :role="to ? undefined : 'button'"
      draggable="false"
      :to="to"
      :title="label"
      @click.stop="emit('toggle')"
    >
      <div
        v-if="icon"
        class="relative grid size-7 shrink-0 place-content-center rounded-lg transition-colors"
        :class="
          isActive || hasActiveChild
            ? 'bg-n-brand/15 text-n-brand'
            : 'bg-n-alpha-1 text-n-slate-10 group-hover:bg-n-alpha-2 group-hover:text-n-brand'
        "
      >
        <Icon v-if="icon" :icon="icon" class="size-4" />
        <span
          v-if="showBadge"
          class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
        />
      </div>
      <div
        class="flex min-w-0 flex-1 flex-grow items-center justify-between gap-1.5"
      >
        <span
          class="truncate"
          :class="{
            'text-body-main': !isActive,
            'font-medium text-sm': isActive || hasActiveChild,
          }"
        >
          {{ label }}
        </span>
        <span
          v-if="dynamicCount && !expandable"
          class="inline-grid h-5 min-w-5 place-items-center rounded-full bg-n-slate-4 px-1 text-xxs font-medium leading-3 text-n-slate-12 dark:bg-n-slate-5 flex-shrink-0"
        >
          {{ count }}
        </span>
      </div>
      <span
        v-if="expandable"
        class="size-3 shrink-0"
        :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        @click.stop="emit('toggle')"
      />
    </component>
  </div>
</template>
